import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
  Area,
  ComposedChart,
  Legend,
} from 'recharts'
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { api } from '../../lib/api'

interface GradeHistoryPoint {
  date: string | null
  score: number | null
  grade?: string | null
  count?: number
}

type SeriesKey = 'overall' | 'written_assessments' | 'fact_assessments' | 'homework'

const SERIES_CONFIG: Record<SeriesKey, { label: string; color: string }> = {
  overall:             { label: 'Overall',  color: '#3b82f6' },
  written_assessments: { label: 'Written',  color: '#ef4444' },
  fact_assessments:    { label: 'Fact',     color: '#f59e0b' },
  homework:            { label: 'Homework', color: '#22c55e' },
}

const DEFAULT_SELECTED: SeriesKey[] = ['overall', 'written_assessments', 'fact_assessments']

interface ChartRow {
  date: string
  overall?: number | null
  written_assessments?: number | null
  fact_assessments?: number | null
  homework?: number | null
  counts?: Partial<Record<SeriesKey, number>>
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number | null
    color: string
    dataKey: string
    payload: ChartRow
  }>
  label?: string
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null
  const row = payload[0].payload
  return (
    <div className="bg-surface-base border border-gray-600 rounded-lg p-3 shadow-xl min-w-[160px]">
      <p className="text-sm text-gray-400 mb-2">
        {label && new Date(label).toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })}
      </p>
      <div className="space-y-1">
        {payload.map((entry) => {
          const k = entry.dataKey as SeriesKey
          const count = row.counts?.[k]
          return (
            <div key={entry.dataKey} className="flex items-center justify-between gap-3 text-sm">
              <span className="flex items-center gap-2 text-gray-300">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                {SERIES_CONFIG[k]?.label ?? entry.name}
              </span>
              <span className={`font-bold ${getGradeColor(entry.value)}`}>
                {entry.value !== null && entry.value !== undefined
                  ? `${entry.value.toFixed(1)}%`
                  : '—'}
                {typeof count === 'number' && count > 1 && (
                  <span className="text-xs text-gray-500 ml-1">({count})</span>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function getGradeColor(score: number | null | undefined): string {
  if (score === null || score === undefined) return 'text-gray-500'
  if (score >= 90) return 'text-green-400'
  if (score >= 80) return 'text-blue-400'
  if (score >= 70) return 'text-yellow-400'
  return 'text-red-400'
}

type Timeframe = 30 | 60 | 90 | 'start'

const TIMEFRAME_OPTIONS: { label: string; value: Timeframe }[] = [
  { label: 'Last 30d', value: 30 },
  { label: 'Last 60d', value: 60 },
  { label: 'Last 90d', value: 90 },
  { label: 'Since Start', value: 'start' },
]

interface CourseGradeChartProps {
  courseId: number
  courseName?: string
  currentScore?: number | null
}

interface CourseGradeHistoryResponse {
  grade_history?: GradeHistoryPoint[]
  category_grade_history?: Partial<Record<SeriesKey, GradeHistoryPoint[]>>
}

export function CourseGradeChart({
  courseId,
  courseName,
  currentScore,
}: CourseGradeChartProps) {
  const [timeframe, setTimeframe] = useState<Timeframe>(30)
  const [selected, setSelected] = useState<Set<SeriesKey>>(new Set(DEFAULT_SELECTED))

  const { data, isLoading } = useQuery<CourseGradeHistoryResponse>({
    queryKey: ['course-grade-history', courseId, timeframe],
    queryFn: async () => {
      const params =
        timeframe === 'start' ? 'since_start=true' : `days=${timeframe}`
      const response = await api.get(
        `/api/parent/courses/${courseId}?${params}`
      )
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const gradeHistory = data?.grade_history ?? []
  const categoryHistory = data?.category_grade_history ?? {}

  const seriesData: Record<SeriesKey, GradeHistoryPoint[]> = useMemo(() => ({
    overall: gradeHistory,
    written_assessments: categoryHistory.written_assessments ?? [],
    fact_assessments: categoryHistory.fact_assessments ?? [],
    homework: categoryHistory.homework ?? [],
  }), [gradeHistory, categoryHistory])

  const hasSeries = (k: SeriesKey) => (seriesData[k]?.length ?? 0) > 0

  // Auto-drop empty series from the selection.
  useEffect(() => {
    const next = new Set<SeriesKey>()
    for (const k of selected) if (hasSeries(k)) next.add(k)
    if (next.size === 0) {
      for (const k of DEFAULT_SELECTED) if (hasSeries(k)) next.add(k)
    }
    if (next.size !== selected.size) setSelected(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Merge each series' daily points into a single date-keyed chart row.
  const chartData: ChartRow[] = useMemo(() => {
    const byDate = new Map<string, ChartRow>()
    const keys: SeriesKey[] = ['overall', 'written_assessments', 'fact_assessments', 'homework']

    for (const key of keys) {
      // Dedup to one point per day (latest snapshot wins).
      const latestPerDay = new Map<string, GradeHistoryPoint>()
      for (const point of seriesData[key]) {
        if (!point.date || point.score === null) continue
        const day = point.date.slice(0, 10)
        const prev = latestPerDay.get(day)
        if (!prev || (prev.date ?? '') < point.date) {
          latestPerDay.set(day, point)
        }
      }
      for (const [day, point] of latestPerDay) {
        if (!byDate.has(day)) byDate.set(day, { date: day, counts: {} })
        const row = byDate.get(day)!
        row[key] = point.score
        if (point.count !== undefined && row.counts) row.counts[key] = point.count
      }
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date))
  }, [seriesData])

  const selectedList = useMemo(() =>
    (['overall', 'written_assessments', 'fact_assessments', 'homework'] as SeriesKey[])
      .filter((k) => selected.has(k)),
    [selected])

  if (isLoading) {
    return (
      <div className="card animate-pulse">
        <div className="h-5 w-40 bg-surface-card rounded mb-4" />
        <div className="h-72 bg-surface-card rounded" />
      </div>
    )
  }

  // Need at least 2 data points across the selected series to draw a trend.
  const totalPoints = chartData.filter(row =>
    selectedList.some(k => row[k] !== undefined && row[k] !== null)
  ).length
  if (totalPoints < 2) return null

  // Trend: based on Overall if selected, else first selected series.
  const trendKey: SeriesKey = selectedList.includes('overall') ? 'overall' : selectedList[0]
  const trendSeries = chartData
    .map((r) => r[trendKey])
    .filter((v): v is number => typeof v === 'number')
  const firstScore = trendSeries.length ? trendSeries[0] : null
  const lastScore = trendSeries.length ? trendSeries[trendSeries.length - 1] : null
  const trendChange = firstScore !== null && lastScore !== null ? lastScore - firstScore : null

  const toggle = (k: SeriesKey) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(k)) {
        if (next.size > 1) next.delete(k) // keep at least one selected
      } else {
        next.add(k)
      }
      return next
    })
  }

  const showFill = selectedList.length === 1

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-quest-primary" />
          <div>
            <h2 className="text-lg font-bold">Grade History</h2>
            {courseName && (
              <p className="text-sm text-gray-400">{courseName}</p>
            )}
          </div>
        </div>
        {trendChange !== null && (
          <div className="flex items-center gap-2">
            {trendChange >= 2 ? (
              <TrendingUp className="w-5 h-5 text-green-400" />
            ) : trendChange <= -2 ? (
              <TrendingDown className="w-5 h-5 text-red-400" />
            ) : (
              <Minus className="w-5 h-5 text-gray-400" />
            )}
            <div className="text-right">
              <div
                className={`font-bold ${
                  trendChange >= 2
                    ? 'text-green-400'
                    : trendChange <= -2
                      ? 'text-red-400'
                      : 'text-gray-400'
                }`}
              >
                {trendChange >= 0 ? '+' : ''}
                {trendChange.toFixed(1)}%
              </div>
              <p className="text-xs text-gray-500">
                {trendChange >= 2
                  ? 'Improving'
                  : trendChange <= -2
                    ? 'Declining'
                    : 'Stable'}
                <span className="ml-1 text-gray-600">({SERIES_CONFIG[trendKey].label})</span>
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Timeframe selector */}
      <div className="flex gap-1 mb-3">
        {TIMEFRAME_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setTimeframe(opt.value)}
            className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
              timeframe === opt.value
                ? 'bg-quest-primary text-white'
                : 'bg-surface-card text-gray-400 hover:text-gray-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Series multi-toggle */}
      <div className="flex flex-wrap gap-1 mb-3">
        {(['overall', 'written_assessments', 'fact_assessments', 'homework'] as SeriesKey[])
          .filter(hasSeries)
          .map((k) => {
            const isOn = selected.has(k)
            const cfg = SERIES_CONFIG[k]
            return (
              <button
                key={k}
                onClick={() => toggle(k)}
                className={`px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1.5 border ${
                  isOn
                    ? 'text-white border-transparent'
                    : 'bg-surface-card text-gray-400 hover:text-gray-200 border-gray-700'
                }`}
                style={isOn ? { backgroundColor: cfg.color } : undefined}
                aria-pressed={isOn}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: isOn ? 'rgba(255,255,255,0.9)' : cfg.color }}
                />
                {cfg.label}
              </button>
            )
          })}
      </div>

      <div className="h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 0, bottom: 5 }}
          >
            <defs>
              {selectedList.map((k) => (
                <linearGradient key={k} id={`grad-${k}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={SERIES_CONFIG[k].color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={SERIES_CONFIG[k].color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="date"
              stroke="#9ca3af"
              fontSize={11}
              tickFormatter={(value) =>
                new Date(value).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })
              }
            />
            <YAxis
              domain={[
                (dataMin: number) => Math.max(0, Math.floor(dataMin - 3)),
                (dataMax: number) => Math.min(100, Math.ceil(dataMax + 3)),
              ]}
              stroke="#9ca3af"
              fontSize={11}
              tickFormatter={(value) => `${value}%`}
              width={42}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={24}
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, color: '#9ca3af' }}
              formatter={(value) => SERIES_CONFIG[value as SeriesKey]?.label ?? value}
            />
            <ReferenceLine y={70} stroke="#f59e0b" strokeDasharray="5 5" strokeOpacity={0.5}>
              <Label value="C" position="left" fill="#f59e0b" fontSize={10} />
            </ReferenceLine>
            <ReferenceLine y={90} stroke="#22c55e" strokeDasharray="5 5" strokeOpacity={0.5}>
              <Label value="A" position="left" fill="#22c55e" fontSize={10} />
            </ReferenceLine>
            {showFill && (
              <Area
                type="monotone"
                dataKey={selectedList[0]}
                stroke="none"
                fill={`url(#grad-${selectedList[0]})`}
                connectNulls
                isAnimationActive={false}
                legendType="none"
              />
            )}
            {selectedList.map((k) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                name={k}
                stroke={SERIES_CONFIG[k].color}
                strokeWidth={2}
                dot={{ fill: SERIES_CONFIG[k].color, strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: SERIES_CONFIG[k].color }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Stats row */}
      <div className="flex justify-between mt-4 pt-3 border-t border-gray-700 text-sm">
        <div>
          <p className="text-gray-500">Start</p>
          <p className={`font-bold ${getGradeColor(firstScore)}`}>
            {firstScore !== null ? `${firstScore.toFixed(1)}%` : 'N/A'}
          </p>
        </div>
        <div className="text-center">
          <p className="text-gray-500">Data Points</p>
          <p className="font-bold text-gray-300">{totalPoints}</p>
        </div>
        <div className="text-right">
          <p className="text-gray-500">Current</p>
          <p className={`font-bold ${getGradeColor(currentScore ?? lastScore)}`}>
            {currentScore !== null && currentScore !== undefined
              ? `${currentScore.toFixed(1)}%`
              : lastScore !== null
                ? `${lastScore.toFixed(1)}%`
                : 'N/A'}
          </p>
        </div>
      </div>
    </div>
  )
}

export default CourseGradeChart
